/**
 * <composer-assortiment> — « Composez votre assortiment » (Web Component natif, zéro dépendance).
 *
 * Amélioration progressive : sans JavaScript, chaque produit a son mini-formulaire
 * natif (quantité + bouton) → ajout un par un. Ce composant transforme la grille en
 * sélecteur unique : compteur/sous-total en direct, progression des paliers de remise,
 * et un seul bouton « Ajouter au panier (N) » qui ajoute tout en une requête.
 *
 * L'ajout groupé remplit un <form action="/cart/add"> caché puis le soumet :
 * l'intercepteur global de cart-items.js s'en charge (AJAX + re-rendu du drawer +
 * ouverture). Aucune logique panier dupliquée ici.
 */
class ComposerAssortiment extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-composer-item]'));
    this.summary = this.querySelector('[data-composer-summary]');
    this.batchForm = this.querySelector('[data-composer-batch-form]');
    this.addAllBtn = this.querySelector('[data-composer-add-all]');
    this.countEl = this.querySelector('[data-composer-count]');
    this.subtotalEl = this.querySelector('[data-composer-subtotal]');
    this.addUrl = this.getAttribute('data-add-url') || '/cart/add';

    // Formatteur monétaire (sous-total indicatif, hors remise appliquée au panier).
    const currency = this.getAttribute('data-currency') || 'EUR';
    const locale = this.getAttribute('data-locale') || 'fr';
    try {
      this.money = new Intl.NumberFormat(locale, { style: 'currency', currency });
    } catch (error) {
      this.money = null;
    }

    // Barre de paliers (peut être absente si désactivée).
    this.initVolumeBar();

    // Mode JS : on repart d'une sélection vide (le HTML met 1 par défaut pour le repli sans JS).
    this.items.forEach((item) => {
      const qty = item.querySelector('[data-composer-qty]');
      if (qty) qty.value = '0';
      // Le bouton d'ajout par carte ne sert qu'au repli sans JS.
      item.querySelector('[data-composer-add-one]')?.setAttribute('hidden', '');
    });

    // Révèle le récapitulatif collant (masqué par défaut pour le repli sans JS).
    this.summary?.removeAttribute('hidden');

    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('change', (event) => this.onQtyChange(event));
    // Enter dans un champ quantité ne doit pas déclencher un ajout individuel en mode JS.
    this.addEventListener('submit', (event) => {
      if (event.target.closest('[data-composer-form]')) event.preventDefault();
    });
    this.addAllBtn?.addEventListener('click', () => this.addAll());

    this.update();
  }

  initVolumeBar() {
    this.bar = this.querySelector('[data-volume-bar]');
    if (!this.bar) return;
    try {
      this.tiers = JSON.parse(this.bar.getAttribute('data-volume-tiers') || '[]');
    } catch (error) {
      this.tiers = [];
    }
    this.tplOne = this.bar.getAttribute('data-tpl-one') || '';
    this.tplOther = this.bar.getAttribute('data-tpl-other') || '';
    this.tplMax = this.bar.getAttribute('data-tpl-max') || '';
    this.tplReached = this.bar.getAttribute('data-tpl-reached') || '';
    this.statusEl = this.bar.querySelector('[data-volume-status]');
    this.fillEl = this.bar.querySelector('[data-volume-fill]');
    this.trackEl = this.bar.querySelector('[data-volume-track]');
    this.chips = Array.from(this.bar.querySelectorAll('[data-tier-qty]'));
  }

  onClick(event) {
    const stepper = event.target.closest('[data-qty-minus], [data-qty-plus]');
    if (!stepper) return;
    const input = stepper.parentElement.querySelector('[data-composer-qty]');
    if (!input) return;
    const step = stepper.hasAttribute('data-qty-plus') ? 1 : -1;
    input.value = Math.max(0, (parseInt(input.value, 10) || 0) + step);
    this.update();
  }

  onQtyChange(event) {
    const input = event.target.closest('[data-composer-qty]');
    if (!input) return;
    input.value = Math.max(0, parseInt(input.value, 10) || 0);
    this.update();
  }

  /** Recalcule compteur, sous-total, état du bouton et barre de paliers. */
  update() {
    let totalQty = 0;
    let subtotalCents = 0;
    this.items.forEach((item) => {
      const qty = parseInt(item.querySelector('[data-composer-qty]')?.value, 10) || 0;
      const price = parseInt(item.dataset.unitPrice, 10) || 0;
      totalQty += qty;
      subtotalCents += qty * price;
    });

    if (this.countEl) this.countEl.textContent = String(totalQty);
    if (this.subtotalEl) {
      this.subtotalEl.textContent = this.money
        ? this.money.format(subtotalCents / 100)
        : (subtotalCents / 100).toFixed(2);
    }
    if (this.addAllBtn) this.addAllBtn.disabled = totalQty === 0;

    this.updateVolumeBar(totalQty);
  }

  updateVolumeBar(qty) {
    if (!this.bar || !this.tiers.length) return;

    let reachedLabel = '';
    let next = null;
    this.tiers.forEach((tier) => {
      if (tier.qty <= qty) reachedLabel = tier.label;
      if (tier.qty > qty && !next) next = tier;
    });

    if (this.statusEl) {
      if (next) {
        const remaining = next.qty - qty;
        const tpl = remaining === 1 ? this.tplOne : this.tplOther;
        const nextText = tpl.replace('%count%', remaining).replace('%label%', next.label);
        // Confirme d'abord le palier déjà acquis (cf. volume-tier-bar.liquid), puis annonce le suivant.
        this.statusEl.textContent =
          reachedLabel && this.tplReached
            ? `${this.tplReached.replace('%label%', reachedLabel)} · ${nextText}`
            : nextText;
      } else {
        this.statusEl.textContent = this.tplMax.replace('%label%', reachedLabel);
      }
    }

    const progress = next ? Math.min(100, Math.round((qty / next.qty) * 100)) : 100;
    if (this.fillEl) this.fillEl.style.width = `${progress}%`;
    this.trackEl?.setAttribute('aria-valuenow', String(progress));

    this.chips.forEach((chip) => {
      const tierQty = parseInt(chip.dataset.tierQty, 10) || 0;
      chip.classList.toggle('is-reached', tierQty <= qty);
      chip.classList.toggle('is-next', next != null && tierQty === next.qty);
    });
  }

  /** Ajoute tous les produits sélectionnés (qty > 0) en une requête, via le formulaire caché. */
  addAll() {
    if (!this.batchForm) return;
    this.batchForm.replaceChildren();

    let index = 0;
    this.items.forEach((item) => {
      const qty = parseInt(item.querySelector('[data-composer-qty]')?.value, 10) || 0;
      const id = item.querySelector('[data-composer-variant]')?.value;
      if (qty <= 0 || !id) return;
      this.batchForm.appendChild(this.hiddenInput(`items[${index}][id]`, id));
      this.batchForm.appendChild(this.hiddenInput(`items[${index}][quantity]`, String(qty)));
      index += 1;
    });

    if (index === 0) return;
    // Soumission → intercepteur global (cart-items.js) : AJAX + re-rendu du drawer + ouverture.
    if (typeof this.batchForm.requestSubmit === 'function') {
      this.batchForm.requestSubmit();
    } else {
      this.batchForm.submit();
    }

    // Réinitialise la sélection (le drawer prend le relais).
    this.items.forEach((item) => {
      const qtyInput = item.querySelector('[data-composer-qty]');
      if (qtyInput) qtyInput.value = '0';
    });
    this.update();
  }

  hiddenInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    return input;
  }
}

if (!customElements.get('composer-assortiment')) {
  customElements.define('composer-assortiment', ComposerAssortiment);
}
